import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, UserMinus, Calendar, Users, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";

const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";

const UserProfileModal = ({ userId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [activeTab, setActiveTab] = useState("about");

  useEffect(() => {
    if (isOpen && userId) {
      loadProfile();
    }
  }, [isOpen, userId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${userId}/profile`, { headers });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error("Failed to load profile");
        onClose();
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadFollowers = async () => {
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${userId}/followers`, { headers });
      if (response.ok) {
        setFollowers(await response.json());
      }
    } catch (error) {
      console.error("Error loading followers:", error);
    }
  };

  const loadFollowing = async () => {
    try {
      const headers = currentUser ? await authHeaders() : {};
      const response = await fetch(`${API}/users/${userId}/following`, { headers });
      if (response.ok) {
        setFollowing(await response.json());
      }
    } catch (error) {
      console.error("Error loading following:", error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "followers" && followers.length === 0) {
      loadFollowers();
    } else if (tab === "following" && following.length === 0) {
      loadFollowing();
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error("Please login to follow users");
      return;
    }

    setFollowLoading(true);
    try {
      const headers = await authHeaders();
      const method = profile.is_following ? "DELETE" : "POST";
      const response = await fetch(`${API}/users/${userId}/follow`, { method, headers });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          is_following: data.is_following,
          followers_count: prev.followers_count + (data.is_following ? 1 : -1)
        }));
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      } else {
        toast.error("Failed to update follow status");
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    } finally {
      setFollowLoading(false);
    }
  };

  const handleFollowFromList = async (targetUserId, isCurrentlyFollowing) => {
    if (!currentUser) {
      toast.error("Please login to follow users");
      return;
    }

    try {
      const headers = await authHeaders();
      const method = isCurrentlyFollowing ? "DELETE" : "POST";
      const response = await fetch(`${API}/users/${targetUserId}/follow`, { method, headers });
      
      if (response.ok) {
        const data = await response.json();
        // Update the lists
        setFollowers(prev => prev.map(u => 
          u.user_id === targetUserId ? { ...u, is_following: data.is_following } : u
        ));
        setFollowing(prev => prev.map(u => 
          u.user_id === targetUserId ? { ...u, is_following: data.is_following } : u
        ));
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    }
  };

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : profile ? (
          <>
            {/* Header */}
            <div className="p-6 pb-4 text-center border-b border-border">
              <Avatar className="h-20 w-20 mx-auto mb-3">
                {profile.picture && <AvatarImage src={profile.picture} />}
                <AvatarFallback className="text-2xl">{getInitials(profile.name)}</AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold">{profile.name}</h2>
              {profile.bio && (
                <p className="text-sm text-muted-foreground mt-1">{profile.bio}</p>
              )}
              
              {/* Stats */}
              <div className="flex items-center justify-center gap-6 mt-4">
                <button 
                  onClick={() => handleTabChange("followers")}
                  className="text-center hover:text-primary transition-colors"
                >
                  <p className="text-xl font-bold">{profile.followers_count}</p>
                  <p className="text-xs text-muted-foreground">Followers</p>
                </button>
                <button 
                  onClick={() => handleTabChange("following")}
                  className="text-center hover:text-primary transition-colors"
                >
                  <p className="text-xl font-bold">{profile.following_count}</p>
                  <p className="text-xs text-muted-foreground">Following</p>
                </button>
                <div className="text-center">
                  <p className="text-xl font-bold">{profile.games_attended}</p>
                  <p className="text-xs text-muted-foreground">Games</p>
                </div>
              </div>

              {/* Follow Button */}
              {!isOwnProfile && currentUser && (
                <Button
                  className="mt-4 w-full"
                  variant={profile.is_following ? "outline" : "default"}
                  onClick={handleFollow}
                  disabled={followLoading}
                >
                  {followLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : profile.is_following ? (
                    <><UserMinus className="w-4 h-4 mr-2" />Unfollow</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" />Follow</>
                  )}
                </Button>
              )}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="w-full grid grid-cols-3 rounded-none border-b border-border bg-transparent h-auto p-0">
                <TabsTrigger 
                  value="about" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
                >
                  About
                </TabsTrigger>
                <TabsTrigger 
                  value="followers"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
                >
                  Followers
                </TabsTrigger>
                <TabsTrigger 
                  value="following"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
                >
                  Following
                </TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="p-4 space-y-3 m-0">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>Hosted {profile.games_hosted} games</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>Attended {profile.games_attended} games</span>
                </div>
              </TabsContent>

              <TabsContent value="followers" className="m-0 max-h-64 overflow-y-auto">
                {followers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">No followers yet</p>
                ) : (
                  <div className="divide-y divide-border">
                    {followers.map((f) => (
                      <UserListItem 
                        key={f.user_id} 
                        user={f} 
                        currentUserId={currentUser?.user_id}
                        onFollow={() => handleFollowFromList(f.user_id, f.is_following)}
                        onViewProfile={() => {
                          onClose();
                          // Small delay to let modal close, then reopen with new user
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('openUserProfile', { detail: f.user_id }));
                          }, 200);
                        }}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="following" className="m-0 max-h-64 overflow-y-auto">
                {following.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">Not following anyone yet</p>
                ) : (
                  <div className="divide-y divide-border">
                    {following.map((f) => (
                      <UserListItem 
                        key={f.user_id} 
                        user={f} 
                        currentUserId={currentUser?.user_id}
                        onFollow={() => handleFollowFromList(f.user_id, f.is_following)}
                        onViewProfile={() => {
                          onClose();
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('openUserProfile', { detail: f.user_id }));
                          }, 200);
                        }}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// Small user list item component
const UserListItem = ({ user, currentUserId, onFollow, onViewProfile }) => {
  const isOwnProfile = currentUserId === user.user_id;
  
  return (
    <div className="flex items-center gap-3 p-3 hover:bg-muted transition-colors">
      <button onClick={onViewProfile} className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="h-10 w-10">
          {user.picture && <AvatarImage src={user.picture} />}
          <AvatarFallback className="text-sm">{getInitials(user.name)}</AvatarFallback>
        </Avatar>
        <span className="font-medium truncate">{user.name}</span>
      </button>
      {!isOwnProfile && currentUserId && (
        <Button
          size="sm"
          variant={user.is_following ? "outline" : "default"}
          onClick={onFollow}
          className="shrink-0"
        >
          {user.is_following ? "Following" : "Follow"}
        </Button>
      )}
    </div>
  );
};

export default UserProfileModal;
